import { cap } from '../utils/format';
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search, X } from 'lucide-react';
import api from '../api/client';
import Table from '../components/Table';
import DateRangePicker from '../components/DateRangePicker';

const MODULES = ['assets','employees','auth','tickets','repairs','licenses','accessories','toner','donations','insurance','offboarding','system_users','policies','settings'];
const ACTIONS = ['create','update','delete','login','logout','view','assign','return','password_change','password_reset','upload'];

export default function AuditTrail() {
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (filterModule) params.module = filterModule;
      if (filterAction) params.action = filterAction;
      if (dateRange.from) params.from = dateRange.from;
      if (dateRange.to) params.to = dateRange.to;
      const r = await api.get('/audit', { params });
      let data = r.data.data || [];
      if (search) data = data.filter(row =>
        row.SystemUser?.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        row.description?.toLowerCase().includes(search.toLowerCase()) ||
        row.module?.toLowerCase().includes(search.toLowerCase())
      );
      setRows(data); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, filterModule, filterAction, search, dateRange]);

  useEffect(() => { fetch(); }, [fetch]);

  const resetFilters = () => { setSearch(''); setFilterModule(''); setFilterAction(''); setDateRange({ from: null, to: null }); setPage(1); };
  const hasFilters = search || filterModule || filterAction || dateRange.from || dateRange.to;

  const actionColor = { create: 'text-green-700 bg-green-50', update: 'text-blue-700 bg-blue-50', delete: 'text-red-700 bg-red-50', login: 'text-purple-700 bg-purple-50', logout: 'text-slate-600 bg-slate-100', view: 'text-slate-400 bg-slate-50', assign: 'text-indigo-700 bg-indigo-50', return: 'text-amber-700 bg-amber-50', password_change: 'text-teal-700 bg-teal-50', password_reset: 'text-orange-700 bg-orange-50', upload: 'text-cyan-700 bg-cyan-50' };

  const columns = [
    { header: 'Timestamp', render: r => <span className="text-xs whitespace-nowrap" style={{ color: "#475569" }}>{new Date(r.created_at).toLocaleString()}</span> },
    { header: 'User', render: r => <span className="font-medium text-sm" style={{ color: "#e2e8f0" }}>{r.SystemUser?.full_name || r.SystemUser?.username || '—'}</span> },
    { header: 'Action', render: r => <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${actionColor[r.action] || 'text-slate-600 bg-slate-100'}`}>{r.action}</span> },
    { header: 'Module', render: r => <span className="capitalize text-sm" style={{ color: "#94a3b8" }}>{r.module?.replace(/_/g, ' ')}</span> },
    { header: 'Description', render: r => <span className="text-xs block max-w-xs truncate" style={{ color: "#475569" }}>{r.description || '—'}</span> },
    { header: 'IP', render: r => <span className="text-xs font-mono" style={{ color: "#334155" }}>{r.ip_address || '—'}</span> },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold gradient-text">Audit Trail</h1><p className="text-xs" style={{ color: "#334155" }}>{total} total records</p></div>
        <button onClick={fetch} className="btn-ghost p-2"><RefreshCw size={16} /></button>
      </div>

      <div className="glass glow-border rounded-2xl p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search user, description, module..."
              className="input-dark pl-9" />
          </div>
          <select value={filterModule} onChange={e => { setFilterModule(e.target.value); setPage(1); }} className="input-dark" style={{ width: "auto", minWidth: 150 }}>
            <option value="">All Modules</option>
            {MODULES.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
          </select>
          <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }} className="input-dark" style={{ width: "auto", minWidth: 150 }}>
            <option value="">All Actions</option>
            {ACTIONS.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
          </select>
          <DateRangePicker value={dateRange} onChange={r => { setDateRange(r); setPage(1); }} />
          {hasFilters && <button onClick={resetFilters} className="btn-ghost text-xs" style={{ color: "#ef4444" }}><X size={14} /> Clear</button>}
        </div>
      </div>

      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage} />
    </div>
  );
}
